/**
 * One recall — `/recalls/$recallId`, `screens.md` §11, plan U49.
 *
 * Header, widen, lift, the evidence record and the trail. Lifted recalls render the same
 * screen without widen and lift. Decisions live in `recall-detail.ts`.
 */
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import {
  recallNoticePath,
  type BatchSummary,
  type ProblemBody,
  type RecallDetail as RecallDetailBody,
} from "@packagepage/shared";
import { Button, RecallFlow, Surface } from "@packagepage/ui";

import { problemOf } from "../api/client.js";
import { requestEvidence, type EvidenceFormat } from "../api/evidence.js";
import { api } from "../api/procedures.js";
import { displayName } from "./product-row.js";
import { LiveRecallBanner } from "./RecallBanner.js";
import {
  EVIDENCE_NOTE,
  LIFT_CONSEQUENCE,
  WIDEN_GTIN_NOTE,
  addedLots,
  firstOf,
  liftQuestion,
  liftRecord,
  scopeLabel,
  trailRows,
  when,
  widenBody,
  widenOutcome,
  widenPreflight,
} from "./recall-detail.js";
import {
  CARVE_OUT,
  EXACT_MATCH_WARNING,
  NEW_TO_US,
  lotsNotOnFile,
  noticeAddress,
  splitLots,
} from "./recall-flow.js";
import { actorLabel } from "./audit-log.js";
import { saveBlob } from "./save-blob.js";

import "@packagepage/ui/button.css";
import "./recall-flow.css";
import "./recall-detail.css";

type Recall = RecallDetailBody;

function Refused({ problem }: { problem: Pick<ProblemBody, "title" | "detail"> }) {
  return (
    <Surface tone="error" emphasis="filled" data-recall-state="refused">
      <strong>{problem.title}</strong>
      <p>{problem.detail}</p>
    </Surface>
  );
}

const UNANSWERED = {
  title: "We could not confirm the change",
  detail:
    "The request did not come back with an answer. Reload this page and read the trail " +
    "before trying again.",
};

const UNFETCHED = {
  title: "We could not download the evidence record",
  detail: "The request did not come back with a file. Nothing about the recall changed.",
};

export function RecallHeader({
  recall,
  productName,
  address,
}: {
  recall: Recall;
  productName: string;
  address: string;
}) {
  const published = firstOf(recall.events, "published");
  const lifted = firstOf(recall.events, "lifted");
  const scope = scopeLabel(recall.scope, recall.lots);
  const body = (
    <div className="pp-recall-detail__head" data-recall-status={recall.status}>
      <div className="pp-recall-flow__label">Recall notice</div>
      <div className="pp-recall-flow__title">{productName}</div>
      <div className="pp-recall-flow__meta">
        <span className="pp-recall-flow__code">{recall.gtin14}</span>
        <strong>{recall.status === "active" ? "Live" : "Lifted"}</strong>
      </div>
      <p className="pp-recall-detail__line" data-recall-line="published">
        Published {when(published?.at ?? recall.publishedAt)}
        {published === undefined ? null : ` by ${actorLabel(published.actor)}`} · scoped
        to {scope}
      </p>
      {recall.status === "active" ? (
        <p className="pp-recall-detail__line">
          Scans of affected units reach{" "}
          <span className="pp-recall-flow__code" data-recall-address="">
            {address}
          </span>
        </p>
      ) : (
        <p className="pp-recall-detail__line" data-recall-line="lifted">
          Lifted {when(lifted?.at ?? recall.liftedAt ?? recall.publishedAt)}
          {lifted === undefined ? null : ` by ${actorLabel(lifted.actor)}`}
        </p>
      )}
      <dl className="pp-recall-detail__tiles">
        <div>
          <dt>{recall.scope === "gtin" ? "Scope" : "Lots in scope"}</dt>
          <dd>
            {recall.scope === "gtin" ? "Whole product" : String(recall.lots.length)}
          </dd>
        </div>
        <div data-recall-tile="scans">
          <dt>Scans reached</dt>
          <dd>{String(recall.reach.scans)}</dd>
        </div>
        <div data-recall-tile="last">
          <dt>Last reached</dt>
          <dd>{recall.reach.lastAt === null ? "None yet" : when(recall.reach.lastAt)}</dd>
        </div>
      </dl>
    </div>
  );
  return recall.status === "active" ? (
    <LiveRecallBanner recall={recall} label="Recall detail header">
      {body}
    </LiveRecallBanner>
  ) : (
    <Surface data-screen-label="Recall detail header">{body}</Surface>
  );
}

export function Trail({ recall }: { recall: Recall }) {
  const rows = trailRows(recall.events);
  return (
    <Surface data-screen-label="Recall trail">
      <div className="pp-recall-flow__label">What happened, in order</div>
      <ol className="pp-recall-detail__trail">
        {rows.map((row) => (
          <li key={row.id} data-recall-event={row.kind}>
            <div className="pp-recall-detail__event">
              <strong>{row.label}</strong>
              <time className="pp-recall-flow__code" dateTime={row.at}>
                {row.when}
              </time>
              <span>by {row.by}</span>
            </div>
            {row.summary === "" ? null : <p>{row.summary}</p>}
          </li>
        ))}
      </ol>
    </Surface>
  );
}

export function WidenPanel({
  recall,
  batches,
  text,
  issue,
  outcome,
  problem,
  running,
  onText,
  onAddLots,
  onWholeProduct,
}: {
  recall: Recall;
  batches: readonly BatchSummary[];
  text: string;
  issue: string | null;
  outcome: string | null;
  problem: Pick<ProblemBody, "title" | "detail"> | null;
  running: boolean;
  onText: (text: string) => void;
  onAddLots: () => void;
  onWholeProduct: () => void;
}) {
  if (recall.scope === "gtin") {
    return (
      <Surface data-recall-panel="widen">
        <div className="pp-recall-flow__label">Correct the scope</div>
        <p className="pp-recall-flow__note">
          This notice covers every unit of this product, which is the widest scope there
          is. Lift it when the hazard has passed.
        </p>
      </Surface>
    );
  }

  const typed = splitLots(text).filter((lot) => !recall.lots.includes(lot));
  const fresh = lotsNotOnFile(typed, batches);
  const offered = batches.filter((batch) => !recall.lots.includes(batch.lot));
  const toggle = (lot: string) => {
    const lots = splitLots(text);
    onText(
      (lots.includes(lot) ? lots.filter((l) => l !== lot) : [...lots, lot]).join("\n"),
    );
  };

  return (
    <Surface data-recall-panel="widen">
      <section className="pp-recall-flow__body">
        <div className="pp-recall-flow__label">Correct the scope</div>
        <p className="pp-recall-flow__note">
          Covered now:{" "}
          {recall.lots.map((lot) => (
            <span key={lot} className="pp-recall-flow__code">
              {lot}{" "}
            </span>
          ))}
        </p>
        <label className="pp-recall-flow__field">
          <span>Add lots as they&apos;re printed. One per line, or comma-separated.</span>
          <textarea
            className="pp-recall-flow__lot-input"
            value={text}
            rows={2}
            spellCheck={false}
            autoCapitalize="off"
            autoComplete="off"
            onChange={(e) => onText(e.target.value)}
          />
        </label>
        {offered.length === 0 ? null : (
          <div className="pp-recall-flow__chips" aria-label="From your batch records">
            {offered.map((batch) => (
              <button
                key={batch.id}
                type="button"
                className="pp-recall-flow__chip"
                aria-pressed={splitLots(text).includes(batch.lot)}
                onClick={() => toggle(batch.lot)}
              >
                {batch.lot}
              </button>
            ))}
          </div>
        )}
        <p className="pp-recall-flow__warning" data-recall-warning="exact-match">
          {EXACT_MATCH_WARNING}
        </p>
        {fresh.length === 0 ? null : (
          <Surface tone="neutral" emphasis="flat" data-recall-state="lot-not-on-file">
            <p>
              {fresh.map((lot) => (
                <span key={lot} className="pp-recall-flow__code">
                  {lot}{" "}
                </span>
              ))}
              <strong>New to us</strong>
            </p>
            <p>{NEW_TO_US}</p>
            <p>{CARVE_OUT}</p>
          </Surface>
        )}
        <div className="pp-recall-flow__actions">
          <Button variant="secondary" loading={running} onClick={onAddLots}>
            Add these lots
          </Button>
        </div>

        <div className="pp-recall-flow__nudge">
          <strong>Or cover everything</strong>
          <p data-recall-copy="widen-gtin">
            Widen to the whole product. {WIDEN_GTIN_NOTE}
          </p>
          <Button variant="secondary" loading={running} onClick={onWholeProduct}>
            Widen to the whole product
          </Button>
        </div>

        {issue === null ? null : (
          <Surface tone="error" emphasis="filled">
            <p>{issue}</p>
          </Surface>
        )}
        {problem === null ? null : <Refused problem={problem} />}
        {outcome === null ? null : (
          <p role="status" className="pp-recall-flow__note" data-recall-outcome="widen">
            {outcome}
          </p>
        )}
      </section>
    </Surface>
  );
}

export function LiftPanel({
  recall,
  confirming,
  problem,
  running,
  onAsk,
  onKeep,
  onLift,
}: {
  recall: Recall;
  confirming: boolean;
  problem: Pick<ProblemBody, "title" | "detail"> | null;
  running: boolean;
  onAsk: () => void;
  onKeep: () => void;
  onLift: () => void;
}) {
  return (
    <Surface data-recall-panel="lift">
      <section className="pp-recall-flow__body">
        {confirming ? (
          <>
            <div className="pp-recall-flow__title">
              {liftQuestion(recall.scope, recall.lots)}
            </div>
            <p>{LIFT_CONSEQUENCE}</p>
            <p className="pp-recall-flow__note" data-recall-copy="lift-record">
              {liftRecord(recall.reach.scans)}
            </p>
            <p className="pp-recall-flow__note">Live in about five seconds.</p>
            {problem === null ? null : <Refused problem={problem} />}
            <div className="pp-recall-flow__actions">
              <button type="button" className="pp-recall-flow__link" onClick={onKeep}>
                Keep it live
              </button>
              <Button variant="secondary" loading={running} onClick={onLift}>
                Lift the notice
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="pp-recall-flow__label">Lift the recall</div>
            <p className="pp-recall-flow__note">
              When the hazard has passed. Scans go back to where they went before.
            </p>
            <div className="pp-recall-flow__actions">
              <Button variant="secondary" onClick={onAsk}>
                Lift the recall
              </Button>
            </div>
          </>
        )}
      </section>
    </Surface>
  );
}

export function EvidencePanel({
  problem,
  running,
  onDownload,
}: {
  problem: Pick<ProblemBody, "title" | "detail"> | null;
  running: EvidenceFormat | null;
  onDownload: (format: EvidenceFormat) => void;
}) {
  return (
    <Surface data-recall-panel="evidence">
      <section className="pp-recall-flow__body">
        <div className="pp-recall-flow__label">Evidence record</div>
        <p className="pp-recall-flow__note">{EVIDENCE_NOTE}</p>
        <div className="pp-recall-flow__actions">
          <Button
            variant="secondary"
            loading={running === "pdf"}
            data-recall-evidence="pdf"
            onClick={() => onDownload("pdf")}
          >
            Download evidence record
          </Button>
          <Button
            variant="secondary"
            loading={running === "csv"}
            data-recall-evidence="csv"
            onClick={() => onDownload("csv")}
          >
            Download as CSV
          </Button>
        </div>
        {problem === null ? null : <Refused problem={problem} />}
      </section>
    </Surface>
  );
}

export function RecallDetail({ recallId }: { recallId: string }): ReactNode {
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [issue, setIssue] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const recall = useQuery({
    queryKey: ["recalls", "read", recallId],
    queryFn: () => api.recall(recallId),
  });
  const productId = recall.data?.productId ?? "";
  const product = useQuery({
    queryKey: ["products", "read", productId],
    queryFn: () => api.product(productId),
    enabled: productId !== "",
  });
  const batches = useQuery({
    queryKey: ["batches", productId],
    queryFn: () => api.batches(productId),
    enabled: productId !== "",
  });
  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
  });

  const settle = () => {
    void queryClient.invalidateQueries({ queryKey: ["recalls"] });
    void queryClient.invalidateQueries({ queryKey: ["products"] });
    void queryClient.invalidateQueries({ queryKey: ["batches", productId] });
  };

  const widen = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.widenRecall(recallId, body),
    onSuccess: (response, body) => {
      setOutcome(widenOutcome(response, addedLots(recall.data?.lots ?? [], body)));
      setText("");
      settle();
    },
  });
  const lift = useMutation({
    mutationFn: () => api.liftRecall(recallId),
    onSuccess: () => {
      setConfirming(false);
      settle();
    },
  });
  const evidence = useMutation({
    mutationFn: (format: EvidenceFormat) => requestEvidence(recallId, format),
    onSuccess: (outcome) => {
      if (outcome.ok) saveBlob(outcome.blob, outcome.filename);
    },
  });

  let body: ReactNode;
  if (recall.isPending) {
    body = (
      <Surface>
        <p>Loading this recall&hellip;</p>
      </Surface>
    );
  } else if (recall.error !== null) {
    const problem = problemOf(recall.error);
    body = (
      <Surface tone="error" emphasis="filled">
        <strong>{problem?.title ?? "We could not load this recall"}</strong>
        <p>
          {problem?.detail ??
            "Scans are unaffected — the resolver answers from its own mirror and does not depend on this screen."}
        </p>
        <Link to="/" className="pp-recall-flow__link">
          Back to the dashboard
        </Link>
      </Surface>
    );
  } else {
    const data = recall.data;
    const name =
      product.data === undefined
        ? data.gtin14
        : displayName(product.data.name, product.data.gtin14).text;
    const send = (next: Record<string, unknown>) => {
      setOutcome(null);
      widen.reset();
      const found = widenPreflight(next);
      setIssue(found);
      if (found === null) widen.mutate(next);
    };
    const widenProblem =
      widen.error === null ? null : (problemOf(widen.error) ?? UNANSWERED);
    const liftProblem =
      lift.error === null ? null : (problemOf(lift.error) ?? UNANSWERED);
    const evidenceProblem =
      evidence.error !== null
        ? UNFETCHED
        : evidence.data?.ok === false
          ? (evidence.data.problem ?? UNFETCHED)
          : null;

    body = (
      <>
        <RecallHeader
          recall={data}
          productName={name}
          address={noticeAddress(
            org.data?.resolverBaseUrl ?? "",
            recallNoticePath(data.gtin14, data.id),
            data.externalUrl ?? "",
            data.externalUrl === null ? "builtIn" : "external",
          )}
        />
        {data.status !== "active" ? null : (
          <>
            <WidenPanel
              recall={data}
              batches={batches.data?.batches ?? []}
              text={text}
              issue={issue}
              outcome={outcome}
              problem={widenProblem}
              running={widen.isPending}
              onText={(next) => {
                setIssue(null);
                setText(next);
              }}
              onAddLots={() => send(widenBody(data.lots, { to: "lots", text }))}
              onWholeProduct={() => send(widenBody(data.lots, { to: "gtin" }))}
            />
            <LiftPanel
              recall={data}
              confirming={confirming}
              problem={liftProblem}
              running={lift.isPending}
              onAsk={() => setConfirming(true)}
              onKeep={() => {
                lift.reset();
                setConfirming(false);
              }}
              onLift={() => lift.mutate()}
            />
          </>
        )}
        <EvidencePanel
          problem={evidenceProblem}
          running={evidence.isPending ? evidence.variables : null}
          onDownload={(format) => evidence.mutate(format)}
        />
        <Trail recall={data} />
        <Link
          to="/products/$productId"
          params={{ productId: data.productId }}
          className="pp-recall-flow__link"
        >
          Open the product
        </Link>
      </>
    );
  }

  return (
    <RecallFlow>
      <div data-screen-label="Recall detail" className="pp-recall-flow pp-recall-detail">
        {body}
      </div>
    </RecallFlow>
  );
}
